<template>
  <div :class="['ifs-home-page', { 'locale-ja': localeValue === 'ja' }]">
    <section class="hero">
      <img :src="getImageUrl(pr.heroImagePC)" :alt="pr.heroTitle" class="hero-pc-bg">
      <img :src="getImageUrl(pr.heroImageSP)" :alt="pr.heroTitle" class="hero-sp-bg">
      <div class="hero-mask"></div>
      <div class="inner hero-inner">
        <div class="hero-box">
          <div>
            <div class="hero-title-box">
              <div class="hero-title-content">
                <SIBreadcrumb
                  :breadcrumb="pr.common.breadcrumb"
                  :locale="localeValue"
                  :show-products="false"
                  :show-si="false"
                  :current="pr.heroTitle"
                />
                <h1>{{ pr.heroTitle }}</h1>
                <p class="hero-subtitle">{{ pr.heroSubtitle }}</p>
              </div>
              <img class="hero-partner-sp" :src="getImageUrl(pr.heroPartner)" alt="" aria-hidden="true" />
            </div>
            <p class="hero-desc">{{ pr.heroDesc }}</p>
          </div>
          <img class="hero-partner-pc" :src="getImageUrl(pr.heroPartner)" alt="" aria-hidden="true" />
        </div>
      </div>
    </section>

    <section class="inner official-website">
      <div class="top-content">
        <img :src="require('@/assets/cooperate_logo.svg')" alt="CooperateLogo" />
        <h5 v-viewportChanged="showView" class="hidden-element title">
          {{ pr.corporatePartnerships.title }}
        </h5>
        <component
          :is="'a'"
          target="_blank"
          :href="pr.corporatePartnerships.link.path"
          rel="noopener noreferrer"
          class="link-btn"
        >
          <span v-viewportChanged="showView" class="hidden-element">{{ pr.corporatePartnerships.link.label }}</span>
          <img
            :src="require('@/assets/external_link_icon.svg')"
            alt="CooperateLogo"
            aria-hidden="true"
          />
        </component>
      </div>

      <div class="narrow">
        <p
          v-for="(line, index) in pr.intro"
          :key="`intro-${index}`"
          v-viewportChanged="showView"
          class="hidden-element"
        >
          {{ line }}
        </p>
      </div>
    </section>

    <section class="solutions">
      <div class="inner">
        <SISectionTitle :title="pr.solutionsTitle" :subtitle="pr.solutionsSubtitle" />
        <div v-viewportChanged="showView" class="hidden-element solution-grid">
          <component
            :is="'a'"
            v-for="(item, idx) in pr.solutions"
            :key="item.title"
            :href="item.path"
            :id="idx"
            target="_blank"
            rel="noopener noreferrer"
            class="solution-card"
          >
            <div class="icon-wrap">
              <img :src="getImageUrl(item.icon)" :alt="item.title" />
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
            <p class="learn-more">
              <span>{{ pr.common.learnMore }}</span>
              <img :src="require('@/assets/external_link_icon.svg')" alt="" aria-hidden="true" />
            </p>
          </component>
        </div>
      </div>
    </section>

    <section class="scenarios">
      <div class="inner">
        <SISectionTitle
          :title="pr.scenariosTitle"
          :subtitle="pr.scenariosSubtitle"
          :subdesc="pr.scenariosDescription"
        />
        <div v-viewportChanged="showView" class="hidden-element scenarios-grid">
          <article v-for="item in pr.scenarios" :key="item.title" class="scenarios-card">
            <div class="icon-wrap">
              <img :src="getImageUrl(item.icon)" :alt="item.title" />
            </div>
            <h3>{{ item.title }}</h3>
            <p class="title-en">{{ item.titleEn }}</p>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="learn-more-fenrir inner">
      <SISectionTitle :title="pr.learnMoreFenrir.title" />
      <BaseButton tag="router-link" variant="dark" size="hero" :to="contactLink">
        <span v-viewportChanged="showView" class="hidden-element">
          {{ pr.learnMoreFenrir.contact }}
        </span>
      </BaseButton
      >
    </section>
  </div>
</template>

<script>
import { computed, inject } from 'vue';
import BaseButton from '@/components/common/BaseButton.vue';
import SIBreadcrumb from '@/components/si/SIBreadcrumb.vue';
import SISectionTitle from '@/components/si/SISectionTitle.vue';
import useRenderData from '@/composables/useRenderData';

export default {
  name: 'ifs-page',
  components: {
    BaseButton,
    SIBreadcrumb,
    SISectionTitle,
  },
  setup() {
    const { pr, getImageUrl } = useRenderData('ifs');
    const locale = inject('locale');
    const localeValue = computed(() => locale?.value || '');
    const contactLink = computed(() => ({
      name: 'contact-us',
      params: {
        lang: localeValue.value || undefined,
      },
    }));

    return {
      pr,
      getImageUrl,
      localeValue,
      contactLink,
    };
  },
};
</script>

<style lang="scss" scoped>
.ifs-home-page {
  .inner {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    box-sizing: border-box;
  }

  .hero {
    position: relative;
    background: #222222;
    padding: 66px 0 72px;
    margin-top: 64px;

    .hero-pc-bg,
    .hero-sp-bg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .hero-sp-bg {
      display: none;
    }

    .hero-inner {
      position: relative;
      z-index: 1;

      .hero-box {
        display: flex;
        justify-content: space-between;
        gap: 40px;

        h1 {
          margin: 28px 0 0;
          font-size: 32px;
          line-height: 44px;
          color: #ffffff;
          font-weight: 700;
        }

        .hero-subtitle {
          margin: 8px 0 0;
          color: #a7aeb1;
          font-size: 16px;
          line-height: 24px;
        }

        .hero-desc {
          margin: 18px 0 0;
          max-width: 760px;
          color: rgba(255, 255, 255, 0.88);
          font-size: 16px;
          line-height: 28px;
        }

        .hero-partner-pc {
          width: 174px;
          height: 174px;
          align-self: center;
        }

        .hero-partner-sp {
          display: none;
        }
      }
    }
  }

  .official-website {
    .top-content {
      display: flex;
      flex-direction: column;
      gap: 40px;
      align-items: center;
      margin-top: 84px;
      margin-bottom: 102px;

      > img {
        max-width: 360px;
        width: 100%;
      }

      .title {
        color: #212121;
        text-align: center;
        font-size: 24px;
        font-weight: 700;
        line-height: 42px;
        letter-spacing: 0.396px;
      }

      .link-btn {
        display: flex;
        height: 50px;
        padding: 0 40px;
        background: #6a1fb5;
        color: #fff;
        letter-spacing: -0.15px;
        font-weight: 700;
        gap: 16px;
        justify-content: center;
        align-items: center;
        text-decoration: none;
        border-radius: 4px;

        > img {
          filter: brightness(0) invert(1);
          width: 24px;
        }
      }
    }

    .narrow {
      max-width: 800px;
      margin: auto;

      p {
        margin: 0 0 16px;
        font-size: 16px;
        line-height: 30px;
        color: #212121;
        text-align: center;

        &:last-child {
          margin-bottom: 102px;
        }
      }
    }
  }

  .scenarios,
  .solutions {
    padding: 84px 0;
    background: #f7f9fa;

    .solution-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 24px;
    }

    .scenarios-card,
    .solution-card {
      border: 1px solid #e4e9eb;
      background: #ffffff;
      text-decoration: none;
      padding: 24px;
      display: flex;
      flex-direction: column;
      transition: transform 300ms cubic-bezier(0.165, 0.84, 0.44, 1),
        box-shadow 300ms cubic-bezier(0.165, 0.84, 0.44, 1);

      .icon-wrap {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 24px;
          height: 24px;
          object-fit: contain;
        }
      }

      h3 {
        margin: 16px 0 8px;
        color: #212121;
        font-size: 18px;
        line-height: 28px;
        font-weight: 700;
      }

      p {
        margin: 0;
        color: #666666;
        font-size: 14px;
        line-height: 24px;
      }

      .learn-more {
        margin-top: 18px;
        align-self: flex-end;
        min-height: 50px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        text-decoration: none;
        font-size: 12px;
        color: #1565c0;
        line-height: 18px;
        font-weight: 600;
        transition: background-color 200ms cubic-bezier(0.02, 0.01, 0.47, 1);

        img {
          filter: brightness(0);
          width: 24px;
          object-fit: contain;
          transition: transform 200ms cubic-bezier(0.02, 0.01, 0.47, 1);
        }
      }
    }

    .solution-card:hover {
      opacity: 1;
      cursor: pointer;
      transform: translateY(-4px);
      box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.1);

      .learn-more {
        color: #0f5ab6;
        opacity: 1;

        img {
          transform: translateX(4px);
        }
      }
    }
  }

  .scenarios {
    background: #fff;

    .scenarios-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 24px;

      .icon-wrap {
        width: 56px;
        height: 56px;
        background: #f1f3f4;
        border-radius: 50%;
      }

      h3 {
        margin-top: 28px;
      }

      .title-en {
        color: #74787a;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: -0.312px;
        margin-bottom: 8px;
      }
    }
  }

  .learn-more-fenrir {
    margin-top: 102px;
    margin-bottom: 84px;
    text-align: center;
  }
}

.ifs-home-page.locale-ja {
  h1,
  h2,
  h3,
  p,
  a,
  span,
  strong {
    word-break: normal;
    overflow-wrap: break-word;
    line-break: strict;
  }
}

@media only screen and (max-width: 1024px) {
  .ifs-home-page {
    .solutions {
      .solution-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    .scenarios {
      .scenarios-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }
    }
  }
}

@media only screen and (max-width: 768px) {
  .ifs-home-page {
    .inner {
      padding: 0 12px;
    }

    .hero {
      padding: 52px 0 54px;
      margin-top: 56px;

      .hero-inner {
        .hero-box {
          padding: 0 36px;

          .hero-title-box {
            display: flex;
            justify-content: space-between;

            .hero-title-content {
              display: flex;
              flex-direction: column;
              justify-content: space-between;

              h1 {
                font-size: 28px;
                line-height: 38px;
              }

              .hero-subtitle {
                margin-top: 0;
              }
            }

            .hero-partner-sp {
              display: block;
              width: 124px;
              height: 124px;
            }
          }

          .hero-partner-pc {
            display: none;
          }

          .hero-desc {
            font-size: 14px;
            line-height: 24px;
          }
        }
      }
    }

    .solutions {
      padding: 60px 0;

      .solution-grid {
        grid-template-columns: 1fr;
        gap: 14px;
      }
    }

    .scenarios {
      .scenarios-grid {
        grid-template-columns: 1fr !important;
      }
    }
  }
}

@media only screen and (max-width: 510px) {
  .ifs-home-page {
    .hero {
      .hero-pc-bg {
        display: none;
      }

      .hero-sp-bg {
        display: block;
      }

      .hero-inner {
        .hero-box {
          .hero-title-box {
            display: grid;
            grid-template-columns: 1fr;

            .hero-title-content {
              .hero-subtitle {
                margin-top: 8px;
              }
            }

            .hero-partner-sp {
              margin: 8px auto 0;
            }
          }
        }
      }
    }
  }
}
</style>
